/**
 * @file Hook wrapping the `autoConfigureProduct` callable. Consumer:
 * `useSignifyConfigurationSidebar`, which auto-configures a catalog code before opening punch-out.
 * `execute` rethrows the callable's own error, so this hook adds no message of its own and the
 * caller sees the `HttpsError` intact; `error` stays populated for the sidebar's banner.
 */

import { useCallback } from 'react';
import { SignifyAutoConfigureResponse, SIGNIFY_CONFIG } from '#types/SignifyApi.types';
import { autoConfigureProduct } from '../services/signifyApiService';
import { useSignifyApi } from './useSignifyApi';

export const useSignifyAutoConfigureProduct = () => {
  const api = useSignifyApi<SignifyAutoConfigureResponse>();

  const fetchAutoConfiguredProduct = useCallback(
    async (
      customerId: string,
      distributorId: string,
      catalogCode: string,
      createSession: boolean = false,
      fields: 'BASIC' | 'DEFAULT' | 'FULL' = SIGNIFY_CONFIG.defaultFields,
    ): Promise<SignifyAutoConfigureResponse> =>
      api.execute(() =>
        autoConfigureProduct(customerId, distributorId, catalogCode, createSession, fields),
      ),
    [api],
  );

  return {
    loading: api.loading,
    error: api.error,
    autoConfigureData: api.data,
    fetchAutoConfiguredProduct,
    clearAutoConfigure: api.clear,
  };
};
